import { Instance, applySnapshot, flow, getParent, getSnapshot, types } from 'mobx-state-tree';

import { updateLessonWork } from '@common/api/lessonWorks';
import {
	attachLessonsBooks,
	deleteLesson,
	getLesson,
	invertPublished,
	updateLesson,
} from '@common/api/lessons';
import { ILessonsModel } from '@common/models/Lessons/Lessons';
import Module from '@common/models/Modules/Module';
import Program from '@common/models/Programs/Program';
import RubricGroup from '@common/models/Rubrics/RubricGroup';

import Media from '../Media';

const WorkModel = types
	.model('WorkModel', {
		id: types.identifierNumber,
		description: types.maybeNull(types.string),
		lesson_id: types.maybeNull(types.number),
		lesson_work_type: types.optional(types.string, ''),
		// @TODO fake: remove
		image: types.optional(types.string, ''),
		is_enabled: types.optional(types.boolean, false),
		books: types.optional(types.array(types.frozen()), []),
	})
	.volatile(() => ({
		loading: false,
	}))
	.views(() => ({}))
	.actions((self) => ({
		save: flow(function* (data: any, isSilent: boolean = false) {
			try {
				if (!isSilent) {
					self.loading = true;
				}
				const result = yield updateLessonWork(self.id, data);
				applySnapshot(self, result);
			} finally {
				self.loading = false;
			}
		}),
	}));

const Lesson = types
	.model('Lesson', {
		classwork: types.maybeNull(WorkModel),
		homework: types.maybeNull(WorkModel),
		lessonType: types.maybeNull(
			types.model({
				id: types.identifierNumber,
				name: types.optional(types.string, ''),
			}),
		),
		nextLesson: types.maybeNull(
			types.model({
				id: types.identifierNumber,
				module_id: types.maybeNull(types.number),
				program_id: types.maybeNull(types.number),
				sort_id: types.maybeNull(types.number),
			}),
		),
		name: types.maybeNull(types.optional(types.string, '')),
		// TODO change to identifier and check StudentLesson then
		id: types.number,
		module: types.maybeNull(Module),
		program: types.maybeNull(Program),
		sort_id: types.maybeNull(types.number),
		assessment_type_id: types.maybeNull(types.number),
		weight: types.maybeNull(types.string),
		cover_url: types.maybeNull(types.string),
		cover: types.maybeNull(Media),
		books: types.optional(types.array(types.frozen()), []),
		is_published: types.optional(types.boolean, false),
		is_clil: types.optional(types.boolean, false),
		is_practiced_off: types.optional(types.boolean, false),
		is_exam: types.optional(types.boolean, false),
		rubricUnions: types.optional(types.array(RubricGroup), []),
	})
	.volatile(() => ({
		loading: false,
	}))
	.views(() => ({}))
	.actions((self) => ({
		fetch: flow(function* () {
			self.loading = true;
			try {
				const result = yield getLesson(self.id);
				applySnapshot(self, result);
			} finally {
				self.loading = false;
			}
		}),
		remove: flow(function* () {
			self.loading = true;
			try {
				yield deleteLesson(self.id);
				const parent = getParent<ILessonsModel>(self, 2);
				parent.removeItem(self as ILessonModel);
			} finally {
				self.loading = false;
			}
		}),
		save: flow(function* (data: any, isGetSnapshot: boolean = true) {
			self.loading = true;
			try {
				const result = yield updateLesson(
					self.id,
					isGetSnapshot
						? {
								...getSnapshot(self),
								...data,
							}
						: data,
				);
				applySnapshot(self, result);
			} finally {
				self.loading = false;
			}
		}),
		togglePublished: flow(function* () {
			try {
				self.loading = true;
				const result = yield invertPublished(self.id);
				applySnapshot(self, result);
			} finally {
				self.loading = false;
			}
		}),
		attachBooks: flow(function* (data: any) {
			self.loading = true;
			try {
				const result = yield attachLessonsBooks(self.id, data);
				applySnapshot(self, result);
			} finally {
				self.loading = false;
			}
		}),
	}));

export default Lesson;

export interface ILessonModel extends Instance<typeof Lesson> {}
export interface IWorkModel extends Instance<typeof WorkModel> {}
